import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingBag, MapPin, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { useTenant } from "./tenantContext";

export default function ShopSignup() {
  const nav = useNavigate();
  const { ownerId, slug } = useTenant();
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    address: "",
    pin_code: "",
    password: "",
  });
  const [pinCheck, setPinCheck] = useState(null); // { serviced, area, charge, reason }
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Captcha — signed challenge from the backend + local answer state.
  const [captcha, setCaptcha] = useState({ token: "", question: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer("");
    try {
      const { data } = await api.get("/store/signup/captcha");
      setCaptcha({ token: data.token, question: data.question });
    } catch {
      setCaptcha({ token: "", question: "Could not load captcha. Please refresh." });
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => { refreshCaptcha(); }, [refreshCaptcha]);

  // Live PIN lookup — as soon as 6 digits are entered we ask the tenant's
  // shipping table whether we deliver there. Signup is blocked if not.
  const checkPin = useCallback(async (pin) => {
    const clean = (pin || "").replace(/\D/g, "");
    if (clean.length !== 6) { setPinCheck(null); return; }
    setChecking(true);
    try {
      const params = { pin: clean };
      if (ownerId) params.owner_id = ownerId;
      const { data } = await api.get(`/store/shipping-preview`, { params });
      setPinCheck(data);
    } catch {
      setPinCheck(null);
    } finally {
      setChecking(false);
    }
  }, [ownerId]);

  const submit = async () => {
    const pin = form.pin_code.replace(/\D/g, "");
    if (form.name.trim().length < 2) { toast.error("Enter your full name."); return; }
    const mobDigits = form.mobile.replace(/\D/g, "");
    if (mobDigits.length < 10) { toast.error("Enter a valid mobile number."); return; }
    if (form.address.trim().length < 5) { toast.error("Enter your delivery address."); return; }
    if (pin.length !== 6) { toast.error("PIN must be 6 digits."); return; }
    if (form.password.length < 6) { toast.error("Password must be 6+ characters."); return; }
    const em = form.email.trim();
    if (em && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      toast.error("Enter a valid email or leave it blank.");
      return;
    }
    if (!captcha.token || String(captchaAnswer).trim() === "") {
      toast.error("Please solve the captcha before signing up.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/store/signup", {
        ...form,
        pin_code: pin,
        email: em || undefined,
        owner_id: ownerId || undefined,
        captcha_token: captcha.token,
        captcha_answer: String(captchaAnswer).trim(),
      });
      toast.success(`Welcome, ${form.name.split(" ")[0]}!`);
      nav(slug ? `/shop/${slug}` : "/shop");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed. Try again.");
      // If it was a captcha error, roll a fresh challenge for the retry.
      const msg = String(err?.response?.data?.detail || "").toLowerCase();
      if (msg.includes("captcha")) refreshCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-2">
            <ShoppingBag className="w-6 h-6 text-[var(--shop-primary,#0055FF)]" />
            <span className="font-heading text-xl font-bold">DialFlow Shop</span>
          </div>
          <h1 className="font-heading text-2xl font-bold">Create your account</h1>
          <p className="text-slate-500 text-sm mt-1">
            Sign up to order and track deliveries.
          </p>
        </div>

        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-5 space-y-3">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bipra Sarkar"
                data-testid="shop-signup-name"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Mobile number</Label>
              <Input
                type="tel"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                placeholder="+91 98765 43210"
                data-testid="shop-signup-mobile"
              />
            </div>
            <div className="space-y-2">
              <Label>
                Email <span className="text-slate-400 text-xs">(optional — for order updates)</span>
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                data-testid="shop-signup-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Delivery address</Label>
              <Textarea
                rows={3}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="House / flat, street, landmark, city"
                data-testid="shop-signup-address"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> PIN code
              </Label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={form.pin_code}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm({ ...form, pin_code: v });
                  checkPin(v);
                }}
                placeholder="700001"
                data-testid="shop-signup-pin"
              />
              {checking && (
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Checking delivery…
                </div>
              )}
              {pinCheck && !checking && (
                <div
                  className={
                    "text-xs " + (pinCheck.serviced ? "text-emerald-700" : "text-red-600")
                  }
                  data-testid="shop-signup-pin-status"
                >
                  {pinCheck.serviced
                    ? `✓ We deliver to ${pinCheck.area || "your area"} · shipping ₹${pinCheck.charge}`
                    : `✗ ${pinCheck.reason || "This PIN is not in our service area."}`}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Password (min 6 chars)</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="shop-signup-password"
              />
            </div>

            {/* Captcha — protects against automated signups. */}
            <div className="space-y-2 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Quick check — <span className="mono text-slate-800">{captcha.question || "Loading…"}</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Answer"
                  className="max-w-[140px]"
                  data-testid="shop-signup-captcha-answer"
                />
                <button
                  type="button"
                  onClick={refreshCaptcha}
                  disabled={captchaLoading}
                  className="mono text-[11px] text-slate-500 hover:text-[var(--shop-primary,#0055FF)] inline-flex items-center gap-1"
                  data-testid="shop-signup-captcha-refresh"
                  title="Get a new question"
                >
                  <RefreshCw className={"w-3 h-3 " + (captchaLoading ? "animate-spin" : "")} />
                  New question
                </button>
              </div>
              <p className="text-[10px] text-slate-400">Just to make sure you&apos;re a real human.</p>
            </div>

            <Button
              onClick={submit}
              disabled={submitting || (pinCheck && !pinCheck.serviced)}
              className="w-full bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)] h-11 text-base"
              data-testid="shop-signup-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create account
            </Button>
            <div className="text-center text-xs text-slate-500 pt-1">
              Already have an account?{" "}
              <Link to="/shop/login" className="text-[var(--shop-primary,#0055FF)] hover:underline">
                Log in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
